import type {
  IHandleCreateCampaignTemplate,
  IHandleCreateCampaignVariants,
  IHandleCreateVariantTemplates,
} from '../hooks/interfaces';
import type { ICampaignGroup, ICampaignGroupTemplate } from '~/modules/campaign-group/types';
import type { IHandleUpdateVariantTemplate } from '~/modules/campaign/modules';
import type { ICampaignTemplate } from '~/modules/campaign-template/types';
import type { IVariantTemplate } from '~/modules/campaign/types';
import type { IDisabledEditingKey } from '../constants';

import { isVariantSamePageType } from '~/modules/campaign-template';
import { findVariantByType } from '~/modules/campaign/utils';
import { IVariantType } from '~/modules/campaign/types';
import { deepCopy, parseTemplateInfoBy } from '~/utils';
import { DATA_DISABLE_EDITING } from '../constants';
import { isCampaignGroupDifferent } from '../utils';

export const getDisabledEditing = (
  status: ICampaignGroup['status'] | undefined,
  key: IDisabledEditingKey,
) => {
  if (!status) return;

  return DATA_DISABLE_EDITING[status]?.[key];
};
export const createCampaignFromItem = async ({
  item,
  shopifyBaseThemeId,
  handleCreateCampaignTemplate,
  handleCreateCampaignVariants,
  handleCreateVariantTemplates,
}: {
  item: string;
  shopifyBaseThemeId?: string;
  handleCreateCampaignTemplate: IHandleCreateCampaignTemplate;
  handleCreateCampaignVariants: IHandleCreateCampaignVariants;
  handleCreateVariantTemplates: IHandleCreateVariantTemplates;
}): Promise<ICampaignTemplate | undefined> => {
  try {
    const campaignCreate = await handleCreateCampaignTemplate(item);
    if (!campaignCreate) throw new Error('Failed to create experiment');

    const templateInfo = parseTemplateInfoBy({ templateSuffix: item });
    const { variantA, variantB } = await handleCreateCampaignVariants({
      campaignTemplate: campaignCreate,
      variantA: { templateSuffix: item, templateType: templateInfo?.type },
    });

    if (!variantA || !variantB) throw new Error('Failed to create variants');

    const { variantTemplateA, variantTemplateB } = await handleCreateVariantTemplates({
      variantTemplateA: { variantID: variantA.id, themeID: shopifyBaseThemeId },
      variantTemplateB: { variantID: variantB.id, themeID: shopifyBaseThemeId },
    });

    return {
      ...campaignCreate,
      variants: [
        { ...variantA, template: variantTemplateA },
        { ...variantB, template: variantTemplateB },
      ],
    };
  } catch (error) {
    throw new Error(
      `Failed to create experiment: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
};
export const updateCampaignGroupWithCampaigns = (
  campaignGroup: ICampaignGroup,
  newCampaigns: (ICampaignTemplate | undefined)[],
): ICampaignGroup => {
  const updatedGroup = deepCopy(campaignGroup) || { campaigns: [] };
  const cloneCampaigns = deepCopy(newCampaigns);
  updatedGroup.campaigns = [
    ...(updatedGroup.campaigns || []),
    ...cloneCampaigns.filter((c) => !!c),
  ];
  return updatedGroup;
};
export const updateCampaignGroupWithVariant = (
  campaignGroup: ICampaignGroup,
  updatedVariant: IVariantTemplate,
): ICampaignGroup => {
  const updatedGroup = deepCopy(campaignGroup);
  updatedGroup.campaigns = updatedGroup.campaigns?.map((campaign) => {
    const updatedVariants = campaign.variants?.map((variant) =>
      variant?.id === updatedVariant.id ? { ...variant, ...updatedVariant } : variant,
    );
    return { ...campaign, variants: updatedVariants };
  });

  return updatedGroup;
};
export const updateVariantTemplate = async ({
  variant,
  campaign,
  redirectUrl,
  templateSuffix,
  handleUpdateVariantTemplate,
}: {
  variant: IVariantTemplate | undefined;
  campaign: ICampaignTemplate | undefined;
  templateSuffix: string;
  redirectUrl?: string;
  handleUpdateVariantTemplate: IHandleUpdateVariantTemplate;
}): Promise<void> => {
  if (!templateSuffix) throw new Error('No template selected');
  if (!variant) throw new Error('No variant selected');
  if (!campaign) throw new Error('No experiment selected');

  const templateInfo = parseTemplateInfoBy({ templateSuffix });
  if (!templateInfo) throw new Error('Invalid template selected');

  const updatedVariant = deepCopy(variant);
  updatedVariant.templateSuffix = templateSuffix;
  updatedVariant.templateType = templateInfo.type;

  const isUpdateVariantA = variant.isOrigin;
  const variantA = findVariantByType(campaign, IVariantType.VARIANT_A);
  if (!isUpdateVariantA && variantA && updatedVariant.template) {
    const isSamePageType = isVariantSamePageType([variantA, updatedVariant]);
    updatedVariant.template.redirectURL = isSamePageType ? '' : (redirectUrl ?? '');
  }

  return await handleUpdateVariantTemplate(updatedVariant, campaign);
};

export const detectCGInfoChanged = (
  campaignGroup: ICampaignGroupTemplate,
  storedCampaignGroup?: ICampaignGroup,
): boolean => {
  if (!storedCampaignGroup) return false;

  return isCampaignGroupDifferent(campaignGroup, storedCampaignGroup);
};
